import { API_URL } from '../config';
// src/pages/CompanyRegisterPage.tsx - MODERN MOBILE-FRIENDLY VERSION
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Grid,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
  Fade,
  useTheme,
  useMediaQuery,
  LinearProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  Visibility,
  VisibilityOff,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CompanyRegistration } from '../types';
import AuthPageLayout from '../components/AuthPageLayout';

const steps = ['Company Info', 'Administrator', 'Complete'];

const CompanyRegisterPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<CompanyRegistration>({
    companyName: '',
    companyDomain: '',
    companyEmail: '',
    companyPhone: '',
    companyAddress: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: '',
  });
  
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = (field: keyof CompanyRegistration) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const validateStep = (step: number): boolean => {
    setError(null);
    
    if (step === 0) {
      if (!formData.companyName || !formData.companyDomain || !formData.companyEmail) {
        setError('Please fill in all required company fields');
        return false;
      }
    } else if (step === 1) {
      if (!formData.adminFirstName || !formData.adminLastName || !formData.adminEmail || !formData.adminPassword) {
        setError('Please fill in all required administrator fields');
        return false;
      }
      if (formData.adminPassword !== confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
      if (formData.adminPassword.length < 8) {
        setError('Password must be at least 8 characters long');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === steps.length - 2) {
        handleSubmit();
      } else {
        setActiveStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/register-company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      setSuccess('Company registration successful! Please check your email for verification.');
      setActiveStep(steps.length - 1);
      
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout contentWidth={520}>
      <Typography sx={{ color: '#175CD3', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', mb: 2 }}>GET STARTED WITH SCETRU FLEET</Typography>
      <Typography component="h1" sx={{ fontSize: { xs: 32, lg: 40 }, letterSpacing: '-0.045em', lineHeight: 1.15, fontWeight: 750, mb: 2 }}>Your fleet starts here.</Typography>
      <Typography sx={{ color: '#66758B', fontSize: 15, lineHeight: 1.7, mb: 3 }}>Create your company workspace and bring your vehicles, people, and operations together.</Typography>
      <Stepper activeStep={activeStep} alternativeLabel={isMobile} sx={{ mb: 4 }}>
        {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      <Box>
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                  {success}
                </Alert>
              )}

              {/* Step 0: Company Information */}
              {activeStep === 0 && (
                <Fade in>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <BusinessIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Company Information
                      </Typography>
                    </Box>
                    
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Company name"
                          value={formData.companyName}
                          onChange={handleChange('companyName')}
                          required
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Company domain"
                          placeholder="yourcompany"
                          InputLabelProps={{ shrink: true }}
                          value={formData.companyDomain}
                          onChange={handleChange('companyDomain')}
                          required
                          helperText="e.g., yourcompany"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography variant="caption" color="text.secondary">
                                  .scetru.com
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Company email"
                          type="email"
                          value={formData.companyEmail}
                          onChange={handleChange('companyEmail')}
                          required
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Company Phone"
                          value={formData.companyPhone}
                          onChange={handleChange('companyPhone')}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Company Address"
                          value={formData.companyAddress}
                          onChange={handleChange('companyAddress')}
                          multiline
                          rows={2}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Fade>
              )}

              {/* Step 1: Administrator Information */}
              {activeStep === 1 && (
                <Fade in>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <PersonIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Administrator Information
                      </Typography>
                    </Box>
                    
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="First name"
                          value={formData.adminFirstName}
                          onChange={handleChange('adminFirstName')}
                          required
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Last name"
                          value={formData.adminLastName}
                          onChange={handleChange('adminLastName')}
                          required
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Administrator email"
                          type="email"
                          value={formData.adminEmail}
                          onChange={handleChange('adminEmail')}
                          required
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.adminPassword}
                          onChange={handleChange('adminPassword')}
                          required
                          helperText="Minimum 8 characters"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(!showPassword)} edge="end">
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Confirm password"
                          type={showPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          error={confirmPassword !== '' && formData.adminPassword !== confirmPassword}
                          helperText={confirmPassword !== '' && formData.adminPassword !== confirmPassword ? 'Passwords do not match' : ''}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Fade>
              )}

              {/* Step 2: Success */}
              {activeStep === 2 && (
                <Fade in>
                  <Box textAlign="center" py={4}>
                    <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                      Registration Complete!
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Your company has been registered successfully.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Redirecting to login page...
                    </Typography>
                  </Box>
                </Fade>
              )}

              {/* Navigation Buttons */}
              {activeStep < steps.length - 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0 || loading}
                    startIcon={<ArrowBack />}
                    sx={{ textTransform: 'none' }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={loading}
                    endIcon={activeStep === steps.length - 2 ? <CheckIcon /> : <ArrowForward />}
                    sx={{
                      textTransform: 'none',
                      px: 4,
                      height: 48, borderRadius: 3, fontWeight: 700, boxShadow: '0 4px 10px #175CD322',
                    }}
                  >
                    {loading ? 'Processing...' : activeStep === steps.length - 2 ? 'Register' : 'Next'}
                  </Button>
                </Box>
              )}

              {/* Login Link */}
              {activeStep === 0 && (
                <Box textAlign="center" mt={3}>
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?{' '}
                    <Link
                      component="button"
                      onClick={() => navigate('/login')}
                      sx={{
                        fontWeight: 600,
                        textDecoration: 'none',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Sign In
                    </Link>
                  </Typography>
                </Box>
              )}
      </Box>
    </AuthPageLayout>
  );
};

export default CompanyRegisterPage;
